import type { RDMDOpts } from '@readme/backend/iso';
import type { ReactNode } from 'react';

import loadable from '@loadable/component';

import useAskAIState from '@core/hooks/useAskAIState';
import useDeferForHydrationMismatch from '@core/hooks/useDeferForHydrationMismatch';
import useEnvInfo from '@core/hooks/useEnvInfo';
import { useProjectStore, useSuperHubStore } from '@core/store';
import RDMD, { TOC } from '@ui/RDMD';

import MigrationError from './MigrationError';

const AIDropdown = loadable(() => import('@routes/SuperHub/components/AIDropdown'), { ssr: true });

interface DocBodyProps {
  dehydrated?: { body: string; toc?: string; css?: string };
  docBody: string;
  editUrl: string;
  footer: ReactNode;
  hasMdxError: boolean;
  hideTOC: boolean;
  isAdmin?: boolean;
  mdx: boolean;
  opts: RDMDOpts;
  showAIDropdown?: boolean;
}

const DocBody = ({
  isAdmin,
  hideTOC,
  dehydrated,
  docBody,
  editUrl,
  mdx,
  opts = {},
  footer,
  hasMdxError = false,
  showAIDropdown = false,
}: DocBodyProps) => {
  const { isClient } = useEnvInfo();
  const [isOwlbotOpenCached, isSuperHub] = useSuperHubStore(s => [s.isOwlbotOpenCached, s.isSuperHub]);
  const [aiDropdownEnabled, privacyView] = useProjectStore(s => [
    s.data.appearance.ai?.dropdown === 'enabled',
    s.data.privacy?.view,
  ]);
  const { renderMode } = useAskAIState();
  const isMdx = !hasMdxError && mdx;
  // Mirrors AIDropdown's internal `shouldShowAIDropdown` logic so we can skip mounting the lazy chunk entirely.
  // If you update this condition, also update `shouldShowAIDropdown` in AIDropdown/index.tsx.
  const shouldRenderAIDropdown = isSuperHub
    ? showAIDropdown
    : showAIDropdown || (aiDropdownEnabled && privacyView === 'public');
  const askAiEnabled = renderMode === 'askai';
  // Hide TOC/AIDropdown when the new-experience AI sidebar is open
  const isOwlbotEnabledAndOpen = useDeferForHydrationMismatch(askAiEnabled && isOwlbotOpenCached, false);

  return (
    <div className="grid-container-fluid" id="content-container">
      <section
        className={`content-body grid-${hideTOC ? 100 : 75} ${isOwlbotEnabledAndOpen ? 'expand-on-small-screens' : ''}`}
      >
        {!!hasMdxError && !!editUrl && !!isAdmin && <MigrationError editUrl={editUrl} />}
        <RDMD
          key={isClient ? window.location.pathname : null}
          body={docBody}
          className="rm-Markdown markdown-body ng-non-bindable"
          css={dehydrated?.css}
          dehydrated={dehydrated?.body}
          format={isMdx ? 'mdx' : 'md'}
          opts={opts}
          style={{ marginLeft: 31 }}
        />
        {!!footer && footer}
      </section>
      <section className={`content-toc grid-25 ${isOwlbotEnabledAndOpen ? 'hide-on-small-screens' : ''}`}>
        {shouldRenderAIDropdown && <AIDropdown showAIDropdown={showAIDropdown} />}
        {!hideTOC && <TOC body={docBody} dehydrated={dehydrated?.toc} format={isMdx ? 'mdx' : 'md'} opts={opts} />}
      </section>
    </div>
  );
};

export default DocBody;
